/**
 * <ProseBlock> — render plain text content (the `text` block type).
 * Matches the existing assistant-prose treatment from ChatMessageList's
 * `ASSISTANT_PROSE` constant. Kept as a tiny wrapper so the surrounding
 * block list can be uniformly `block.map(<BlockRenderer />)`.
 */
import type { ReactNode } from "react";
import { PREAMBLE_LEAD, PREAMBLE_USER_MARKER } from "@/lib/aieditor-preamble";
import { stripLexiunInjectedPreamble } from "@/lib/lexiun-injected-preamble";
import { splitFileRefTokens } from "@/lib/chat/filetag-tokens";
import { FileChip } from "./FileChip";

interface ProseBlockProps {
  text: string;
  /** Apply the user-side preamble strips. Two distinct strip passes
   *  fire when this is on:
   *    1. **Lexiun system-injected preamble** — peels any leading
   *       `System: …` side-channel blocks (reassess directives,
   *       `<user_referenced_files>`, `<user_selected_context>`, …),
   *       the `Sender (untrusted metadata)` provenance JSON, and the
   *       inbound timestamp. See [stripLexiunInjectedPreamble] for the
   *       full pattern set + the rationale for stripping at render time
   *       (defense in depth + retroactive cleanup of polluted history).
   *    2. **AIEditor preamble** — peels the inlined
   *       `<current_document>` / `<selected_range>` editor preamble
   *       from a user message; the LLM still sees it on the wire.
   *  Pass order matters: the system preamble wraps the OUTSIDE of the
   *  text, so peel it first; whatever remains may then start with the
   *  AIEditor preamble lead, which the second pass handles. */
  stripUserPreamble?: boolean;
  /** Parse `![alt](data:image/png;base64,…)` tokens inline + render
   *  them as `<img>` elements. Off by default — enabled ONLY for
   *  assistant blocks (via `ChatMessageList`), never for user text.
   *
   *  Motivating use case: the OpenClaw native WhatsApp plugin's
   *  `whatsapp_login` agent tool renders the pairing QR **inline** in
   *  the assistant reply as a data-URI markdown image (relay
   *  `whatsapp-chat-setup-lexide-relay-2026-06-30.md` §2.3). Without
   *  this parse the lawyer sees the raw markdown syntax instead of a
   *  scannable QR.
   *
   *  Scope is intentionally narrow to minimize XSS surface:
   *   - Matches ONLY `data:image/png;base64,<base64>` — no other MIME,
   *     no `http:`/`https:`/`javascript:`/etc. URIs.
   *   - Requires a base64 payload of at least 20 chars (rules out
   *     empty-ish decoys).
   *   - Never runs on user text — user turns render as `whitespace-
   *     pre-wrap` verbatim (the operator's markdown wouldn't render
   *     anyway).
   *  The rest of markdown (bold/italic/links/code/tables) is
   *  intentionally NOT parsed — this is not a general markdown pipe,
   *  just a targeted QR/image affordance. */
  parseInlineImages?: boolean;
  /** Workspace-file-tags: mid-stream flag. When true (the in-flight bubble), an
   *  in-progress `@tag` (or an unterminated fenced block) at the very tail is HELD
   *  so it never flashes/mis-chips; it renders once the stream completes it. Only
   *  meaningful together with `parseInlineImages` (assistant blocks). */
  streaming?: boolean;
}

function stripAIEditorPreamble(text: string): string {
  if (!text.startsWith(PREAMBLE_LEAD)) return text;
  const marker = `\n\n${PREAMBLE_USER_MARKER}`;
  const idx = text.lastIndexOf(marker);
  if (idx < 0) return text;
  return text.slice(idx + marker.length);
}

/** Match `![alt](data:image/png;base64,PAYLOAD)` inline.
 *  - `alt` is optional and matched non-greedily
 *  - MIME is HARDCODED to `image/png` (no template widening)
 *  - PAYLOAD is `[A-Za-z0-9+/=]{20,}` — real base64 + minimum length
 *  Global + non-greedy so multiple images per block split correctly. */
const INLINE_DATA_IMAGE_RE =
  /!\[([^\]]*)\]\((data:image\/png;base64,[A-Za-z0-9+/=]{20,})\)/g;

interface TextToken { type: "text"; value: string }
interface ImageToken { type: "image"; src: string; alt: string }
type ProseToken = TextToken | ImageToken;

/** Split text on data-URI markdown images. Returns a token array
 *  suitable for direct React rendering. If no images match, returns
 *  a single text token (cheap fast-path). */
export function tokenizeInlineDataImages(text: string): ProseToken[] {
  const tokens: ProseToken[] = [];
  let lastIndex = 0;
  INLINE_DATA_IMAGE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = INLINE_DATA_IMAGE_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    tokens.push({ type: "image", alt: match[1] ?? "", src: match[2] ?? "" });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex === 0) return [{ type: "text", value: text }];
  if (lastIndex < text.length) {
    tokens.push({ type: "text", value: text.slice(lastIndex) });
  }
  return tokens;
}

/** Workspace-file-tags: split an assistant text run into plain text + <FileChip>s
 *  (an `@tag` or a bare machine name → a clickable chip showing the tag; an unknown
 *  tag renders as its literal `@text`). `streamingTip` holds an in-progress tag at
 *  the tail. Returns a node array React renders inline. */
function renderFileRefText(value: string, keyPrefix: string, streamingTip: boolean): ReactNode {
  const segs = splitFileRefTokens(value, { streaming: streamingTip });
  return segs.map((seg, j) =>
    typeof seg === "string" ? (
      seg
    ) : (
      <FileChip key={`${keyPrefix}-f${j}`} payload={seg.payload} source={seg.source} />
    ),
  );
}

export function ProseBlock({ text, stripUserPreamble, parseInlineImages, streaming }: ProseBlockProps) {
  let display = text;
  if (stripUserPreamble) {
    display = stripLexiunInjectedPreamble(display);
    display = stripAIEditorPreamble(display);
  }
  if (parseInlineImages) {
    const tokens = tokenizeInlineDataImages(display);
    // Index of the LAST text token — only its tail is the message stream-tip,
    // so the streaming hold applies only there.
    const lastTextIdx = tokens.reduce((acc, t, k) => (t.type === "text" ? k : acc), -1);
    // Fast path: no images — a single text run (still gets the file-ref pass).
    if (tokens.length === 1 && tokens[0].type === "text") {
      return (
        <span data-testid="prose-block" className="whitespace-pre-wrap">
          {renderFileRefText(tokens[0].value, "s", streaming ?? false)}
        </span>
      );
    }
    return (
      <span data-testid="prose-block" className="whitespace-pre-wrap">
        {tokens.map((tok, i) =>
          tok.type === "image" ? (
            <img
              key={`img-${i}`}
              data-testid="prose-block-inline-image"
              src={tok.src}
              alt={tok.alt}
              // QR codes need to render at a usable size; cap at ~256px
              // (typical WhatsApp Web QR) with contained aspect ratio.
              className="my-2 block max-w-[256px] rounded-md border border-cream-300 dark:border-coffee-700"
            />
          ) : (
            <span key={`txt-${i}`}>
              {renderFileRefText(tok.value, `txt-${i}`, (streaming ?? false) && i === lastTextIdx)}
            </span>
          ),
        )}
      </span>
    );
  }
  return (
    <span data-testid="prose-block" className="whitespace-pre-wrap">
      {display}
    </span>
  );
}
